import { useEffect, useState } from 'react';
import { browserLogger } from 'utils/logger/browserLogger';
import { getErrorDetails } from 'utils/getErrorDetails';

import { makePaymentDetails } from 'services/router/routerMakePaymentDetails';
import { AdyenThreeDSProvider } from 'repositories/threeds/providers/AdyenThreeDSProvider';
import { getThreeDsData } from 'services/threeds/getThreeDsData';
import { ParsedThreeDsData } from 'types/threeds';
import { type ThreeDSProviderConfig } from 'repositories/threeds/providers';

const factory = (threeDSProvider: 'adyen' | undefined) => {
  switch (threeDSProvider) {
    case 'adyen':
    default:
      return new AdyenThreeDSProvider();
  }
};

const environment =
  process.env.NEXT_PUBLIC_CLOUD_FRONT_BUNDLE_ENVIRONMENT === 'prod'
    ? 'production'
    : 'test';

export function useThreeDs({
  orderNum,
  elementId,
  onSuccess,
  onError,
  owner
}: {
  orderNum: string;
  elementId: string;
  onSuccess: () => void;
  onError: () => void;
  owner: 'bileto' | 'platform';
}) {
  const [threeDsData, setThreeDsData] = useState<ParsedThreeDsData | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchThreeDsData = async () => {
      try {
        setIsLoading(true);
        const data = await getThreeDsData(orderNum, owner);
        setThreeDsData(data);
      } catch (error) {
        const logger = browserLogger();
        const errorDetails = getErrorDetails(error);

        logger.error(
          {
            message: 'Error fetching ThreeDS data',
            data: { orderNum },
            extra: {
              orderNum,
              error: {
                message: errorDetails.message,
                stack: errorDetails.stack
              }
            }
          },
          'fetchThreeDsData'
        );
        setThreeDsData(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchThreeDsData();
  }, [orderNum, owner]);

  useEffect(() => {
    if (isLoading || !threeDsData) {
      return;
    }

    const logger = browserLogger();
    const threeDSProvider = 'adyen';
    const provider = factory(threeDSProvider);

    // TODO: Define a proper type for state
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const _onSuccess = async (state: any) => {
      // eslint-disable-next-line no-console
      console.log('state _onSuccess', state);
      try {
        await makePaymentDetails(state, orderNum, owner);
        onSuccess();
      } catch (error) {
        const errorDetails = getErrorDetails(error);

        _onError({
          message: errorDetails.message,
          stack: errorDetails.stack,
          fullObjectError: error
        });
      }
    };

    const _onError: ThreeDSProviderConfig['onError'] = async (error) => {
      // eslint-disable-next-line no-console
      console.log('error _onError', error);
      const errorDetails = getErrorDetails(error);

      onError();
      logger.error(
        {
          message: `[DiscoveryNext][3DS] ${window.location.pathname} 3DS Error`,
          data: { orderNum },
          extra: {
            orderNum,
            env: process?.env?.APP_ENVIRONMENT,
            application: 'purchase',
            threeDSProvider,
            threeDsData,
            error: {
              message: errorDetails.message,
              stack: errorDetails.stack,
              fullObjectError: error?.fullObjectError
            }
          }
        },
        'initThreeDS'
      );
    };

    provider.init({
      elementId,
      environment,
      onSuccess: _onSuccess,
      onError: _onError,
      threeDsData: threeDsData || undefined,
      owner
    });
  }, [orderNum, onSuccess, onError, threeDsData, isLoading, owner]);

  return {
    threeDsData,
    isLoading
  };
}
